// src/components/Navbar.js
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Container,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  Divider,
  Menu,
  MenuItem,
  Badge,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { AccountCircle, Notifications } from '@mui/icons-material';
import ChatIcon from '@mui/icons-material/Chat';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { http } from "../lib/axios";
import { useChat } from '../context/ChatContext';

const logoSrc = encodeURI(
  `${process.env.PUBLIC_URL}/ChatGPT Image Jun 9, 2026, 02_41_12 PM.png`
);

function scrollToHash(hash) {
  const id = (hash || '').replace('#', '');
  if (!id) return;

  // a couple tries helps when route just changed and DOM isn’t ready yet
  let tries = 0;
  const t = setInterval(() => {
    const el = document.getElementById(id);
    if (el) {
      clearInterval(t);
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    tries += 1;
    if (tries > 10) clearInterval(t);
  }, 50);
}

export default function Navbar() {
  const token = localStorage.getItem('access_token');
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [userRole, setUserRole] = useState(localStorage.getItem('user_role') || null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const headerRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { setChatDrawerOpen } = useChat();

  // If we land on /#discover, etc., scroll after render
  useEffect(() => {
    if (location.pathname === '/' && location.hash) {
      scrollToHash(location.hash);
    }
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (!token) return;
    const fetchNotifications = async () => {
      try {
        const { data } = await http.get('/notifications/');
        setNotifications(data);
      } catch (err) {
        console.error('Failed to fetch notifications:', err);
      }
    };
    fetchNotifications();
  }, [token]);

  // Try to load role from /api/accounts/me/ if not already known
  useEffect(() => {
    let cancelled = false;
    async function loadRole() {
      if (!token) return;
      if (userRole) return;
      try {
        const { data } = await http.get('/api/accounts/me/');
        if (!cancelled && data?.role) {
          setUserRole(data.role);
          localStorage.setItem('user_role', data.role);
        }
      } catch {
        // ignore
      }
    }
    loadRole();
    return () => { cancelled = true; };
  }, [token, userRole]);

  const unreadCount = notifications.filter(n => !n.is_read).length;

  const handleMenu = (event) => setAnchorEl(event.currentTarget);
  const handleNotifMenu = (event) => setNotifAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleNotifClose = () => setNotifAnchorEl(null);

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user_role');
    window.location.href = '/';
  };

  const handleMarkAsRead = async (id) => {
    try {
      http.post(`/notifications/${id}/mark_read/`, {});
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  };

  const goToDashboard = async () => {
    let role = userRole || localStorage.getItem('user_role');

    if (!role && token) {
      try {
        const { data } = await http.get('/api/accounts/me/');
        role = data?.role;
        if (role) {
          setUserRole(role);
          localStorage.setItem('user_role', role);
        }
      } catch {
        // ignore
      }
    }

    const byRole = {
      admin: '/admin',
      artist: '/dashboard/artist',
      host: '/dashboard/host',
      buyer: '/dashboard/buyer',
    };

    navigate(byRole[role] || '/account');
    handleClose();
  };

  const goHome = useCallback(() => {
    // If already on home, smooth scroll; otherwise navigate to /#intro
    if (location.pathname === '/') {
      scrollToHash('#intro');
    } else {
      navigate('/#intro');
    }
  }, [location.pathname, navigate]);

  const goDiscover = useCallback(() => {
    // If already on home, smooth scroll; otherwise navigate to /#discover
    if (location.pathname === '/') {
      scrollToHash('#discover');
    } else {
      navigate('/#discover');
    }
  }, [location.pathname, navigate]);

  const goHelp = useCallback(() => {
    navigate('/help', {
      state: {
        backgroundLocation: location,
      },
    });
  }, [location, navigate]);

  // Keep a CSS custom property in sync with the navbar height so pages
  // can add matching top padding globally.
  useLayoutEffect(() => {
    const headerEl = headerRef.current;
    if (!headerEl || typeof window === 'undefined') return undefined;

    const updateHeight = () => {
      const height = headerEl.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--navbar-height', `${height}px`);
    };

    updateHeight();

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(updateHeight)
      : null;
    if (resizeObserver) resizeObserver.observe(headerEl);
    window.addEventListener('resize', updateHeight);

    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener('resize', updateHeight);
      document.documentElement.style.removeProperty('--navbar-height');
    };
  }, []);

  const navItems = useMemo(() => {
    if (!token) {
      return [
        { label: 'Home', action: goHome },
        { label: 'Discover Nearby Art', action: goDiscover },
        { label: 'Help', action: goHelp },
        { label: 'Register', action: () => navigate('/register') },
        { label: 'Login', action: () => navigate('/login') },
      ];
    }
    return [
      { label: 'Home', action: goHome },
      { label: 'Discover Nearby Art', action: goDiscover },
      { label: 'Help', action: goHelp },
    ];
  }, [goDiscover, goHelp, goHome, navigate, token]);

  return (
    <Box
      ref={headerRef}
      component="header"
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 2000,
        backdropFilter: 'blur(14px)',
        //borderBottom: '1px solid rgba(255,255,255,0.14)',
        background: 'linear-gradient(to bottom, rgba(44, 44, 49, 0.92), rgba(112, 112, 124, 0.55))',
        fontFamily: '"Helvetica", Arial, sans-serif',
      }}
    >
      <Container
        maxWidth="xl"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: 56,
          py: 1,
        }}
      >
        {/* left aligned logo */}
        <Box
          component={Link}
          to="/"
          sx={{
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            color: '#fff',
          }}
        >
          <img
            src={logoSrc}
            alt="ARTflow Logo"
            style={{ height: 56, width: 65, display: 'block' }}
          />
        </Box>

        {/* desktop right aligned nav */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            gap: 3,
            fontSize: 13,
            textTransform: 'uppercase',
            letterSpacing: 1,
            color: '#fff',
          }}
        >
          {navItems.map((it) => (
            <Typography
              key={it.label}
              component="button"
              onClick={it.action}
              sx={{
                cursor: 'pointer',
                border: 'none',
                background: 'transparent',
                color: 'rgba(255,255,255,0.92)',
                fontFamily: '"Helvetica", Arial, sans-serif',
                '&:hover': { color: '#fff', textDecoration: 'underline' },
              }}
            >
              {it.label}
            </Typography>
          ))}

          {/* logged-in actions */}
          {token && (
            <>
              <IconButton color="inherit" onClick={handleNotifMenu} sx={{ color: '#fff' }}>
                <Badge color="error" variant="dot" invisible={unreadCount === 0}>
                  <Notifications />
                </Badge>
              </IconButton>

              <Menu
                anchorEl={notifAnchorEl}
                open={Boolean(notifAnchorEl)}
                onClose={handleNotifClose}
              >
                {notifications.length === 0 ? (
                  <MenuItem disabled>No notifications</MenuItem>
                ) : (
                  notifications.slice(0, 5).map(n =>
                    !n.is_read ? (
                      <MenuItem key={n.id} onClick={() => handleMarkAsRead(n.id)}>
                        {n.message}
                      </MenuItem>
                    ) : null
                  )
                )}
                <MenuItem onClick={() => navigate('/notifications')}>View All</MenuItem>
              </Menu>

              <IconButton sx={{ color: '#fff' }} onClick={() => setChatDrawerOpen(true)}>
                <ChatIcon />
              </IconButton>

              <IconButton sx={{ color: '#fff' }} onClick={handleMenu}>
                <AccountCircle />
              </IconButton>
              <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
                <MenuItem onClick={goToDashboard}>{userRole === 'admin' ? 'Admin Workspace' : 'My Dashboard'}</MenuItem>
                <MenuItem onClick={handleLogout}>Logout</MenuItem>
              </Menu>
            </>
          )}
        </Box>

        {/* mobile hamburger */}
        <IconButton
          onClick={() => setMobileOpen(true)}
          sx={{ display: { xs: 'inline-flex', md: 'none' }, color: '#fff' }}
        >
          <MenuIcon />
        </IconButton>
      </Container>

      {/* mobile drawer */}
      <Drawer anchor="right" open={mobileOpen} onClose={() => setMobileOpen(false)}>
        <Box sx={{ width: 280, p: 2, fontFamily: '"Helvetica", Arial, sans-serif' }}>
          <Typography sx={{ fontWeight: 800, mb: 1 }}>ARTflow</Typography>
          <Divider sx={{ mb: 1 }} />

          <List>
            {navItems.map((it) => (
              <ListItemButton
                key={it.label}
                onClick={() => {
                  setMobileOpen(false);
                  it.action();
                }}
              >
                <Typography sx={{ fontWeight: 700 }}>{it.label}</Typography>
              </ListItemButton>
            ))}

            {token && (
              <>
                <Divider sx={{ my: 1 }} />
                <ListItemButton
                  onClick={() => {
                    setMobileOpen(false);
                    setChatDrawerOpen(true);
                  }}
                >
                  <Typography sx={{ fontWeight: 700 }}>Messages</Typography>
                </ListItemButton>

                <ListItemButton
                  onClick={() => {
                    setMobileOpen(false);
                    navigate('/notifications');
                  }}
                >
                  <Typography sx={{ fontWeight: 700 }}>
                    Notifications {unreadCount ? `(${unreadCount})` : ''}
                  </Typography>
                </ListItemButton>

                <ListItemButton
                  onClick={() => {
                    setMobileOpen(false);
                    goToDashboard();
                  }}
                >
                  <Typography sx={{ fontWeight: 700 }}>{userRole === 'admin' ? 'Admin Workspace' : 'My Dashboard'}</Typography>
                </ListItemButton>

                <ListItemButton
                  onClick={() => {
                    setMobileOpen(false);
                    handleLogout();
                  }}
                >
                  <Typography sx={{ fontWeight: 700 }}>Logout</Typography>
                </ListItemButton>
              </>
            )}
          </List>
        </Box>
      </Drawer>
    </Box>
  );
}
